// Operations-console endpoints: live board, inbox, search, trip interventions,
// finance and analytics. Shapes mirror apps/backend/src/services/adminOps /
// adminAnalytics / adminTripIntervention.
import { api, unwrap, cleanParams } from "../lib/axios";
import type { ApiEnvelope } from "../types/api";

export type GroupBy = "day" | "week" | "month";
export interface Window {
  from?: string;
  to?: string;
  groupBy?: GroupBy;
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface LiveTrip {
  id: string;
  ref: number | null;
  status: string;
  statusSince: string;
  minutesInStatus: number;
  stuck: string | null;
  createdAt: string;
  rideTypeId: string | null;
  rideType: string | null;
  rider: { name?: string; phone?: string };
  driver: { id: string; name?: string; phone?: string; plate?: string | null } | null;
  pickup: ({ name?: string } & Partial<LatLng>) | null;
  dropoff: ({ name?: string } & Partial<LatLng>) | null;
  paymentMode: string | null;
  fare: number | null;
  /** Part wallet / part cash split, when the trip has one. */
  partPayment: { walletAmount: number; cashAmount: number; walletRefunded: boolean } | null;
  estimatedDuration: number | null;
}

export interface LiveDriver {
  id: string;
  name?: string;
  phone?: string;
  plate: string | null;
  rating: number | null;
  rideTypeId: string | null;
  rideType: string | null;
  state: "idle" | "assigned" | "onTrip";
  tripId: string | null;
  position: LatLng | null;
  stale: boolean;
  lastSeenAt: string | null;
  lockedForDebt: boolean;
}

export interface LiveSos {
  id: string;
  name: string;
  phone: string;
  occurredBy: string;
  occurredAt: string;
  tripId: string | null;
  position: LatLng | null;
}

export interface LiveBoard {
  generatedAt: string;
  stuckRules: Record<string, number>;
  summary: {
    searching: number;
    toPickup: number;
    atPickup: number;
    onTrip: number;
    atDestination: number;
    stuck: number;
    driversIdle: number;
    driversAssigned: number;
    driversOnTrip: number;
    driversStale: number;
    activeSos: number;
  };
  byRideType: {
    id: string;
    name: string;
    idle: number;
    assigned: number;
    onTrip: number;
    searching: number;
  }[];
  trips: LiveTrip[];
  drivers: LiveDriver[];
  sos: LiveSos[];
}

export interface WindowKpis {
  requests: number;
  completed: number;
  cancelled: number;
  notFound: number;
  inFlight: number;
  fulfilmentRate: number | null;
  gmv: number;
  platformEarning: number;
  avgSecondsToAccept: number | null;
}

export interface TodayKpis {
  since: string;
  today: WindowKpis;
  yesterdaySameTime: WindowKpis;
}

export interface DemandPoint extends LatLng {
  outcome: "unserved" | "cancelled" | "completed" | "active";
}

export interface InboxItem {
  id: string;
  title: string;
  detail?: string;
  link: string;
  at?: string;
}

export interface InboxSection {
  key: string;
  title: string;
  severity: "critical" | "high" | "medium" | "low";
  permission: string;
  count: number;
  link: string;
  items: InboxItem[];
}

export interface Inbox {
  generatedAt: string;
  total: number;
  sections: InboxSection[];
}

export interface SearchHit {
  id: string;
  title?: string;
  subtitle?: string;
  status?: string;
  createdAt?: string;
}

export interface SearchResults {
  trips: SearchHit[];
  drivers: SearchHit[];
  riders: SearchHit[];
}

export interface Money {
  trips: number;
  riderPaid: number;
  grossFare: number;
  discount: number;
  driverEarning: number;
  platformCommission: number;
  bookingFee: number;
  latraLevy: number;
  roundingDifference: number;
  vatOnCommission: number;
  vatOnBookingFee: number;
  gatewayFee: number;
  tolls: number;
  platformEarning: number;
  distanceKm: number;
  vat: number;
  rounding: number;
  takeRate: number | null;
  avgFare: number | null;
  avgPlatformPerTrip: number | null;
  avgDriverPerTrip: number | null;
}

export interface Positions {
  driverReceivables: number;
  driversOwing: number;
  driverPayables: number;
  driversOwed: number;
  driversLockedForDebt: number;
  commissionPending: { count: number; amount: number };
  commissionCollected: { count: number; amount: number };
  commissionWaived: { count: number; amount: number };
  payoutsInFlight: { count: number; amount: number };
  riderWalletBalances: number;
}

export interface FinanceOverview {
  window: { from: string; to: string; groupBy: GroupBy };
  previousWindow: { from: string; to: string };
  totals: Money;
  previous: Money;
  series: (Money & { bucket: string })[];
  /** "walletCash" = a part wallet / part cash trip. */
  byPaymentMode: (Money & { key: string })[];
  /** Money by rail; a part-payment trip counts in both wallet and cash. */
  byPaymentLeg: { wallet: number; cash: number; online: number; partPaymentTrips: number };
  byRideType: (Money & { key: string; name: string })[];
  positions: Positions;
}

export interface CashReconciliation {
  window: { from: string; to: string };
  breakdown: { status: string; mode: string; method: string | null; count: number; amount: number }[];
  oldestUncollected: {
    driverId: string;
    name?: string;
    phone?: string;
    count: number;
    amount: number;
    oldest: string;
  }[];
}

export interface OpsFunnel {
  requests: number;
  completed: number;
  cancelled: number;
  notFound: number;
  riderCancelled: number;
  driverCancelled: number;
  fulfilmentRate: number | null;
  unservedRate: number | null;
  cancelRate: number | null;
  avgMinutesToAccept: number | null;
  avgMinutesToArrive: number | null;
  avgMinutesWaitAtPickup: number | null;
  avgMinutesOnTrip: number | null;
  avgPromisedEta: number | null;
}

export interface OpsAnalytics {
  window: { from: string; to: string; groupBy: GroupBy };
  previousWindow: { from: string; to: string };
  totals: OpsFunnel;
  previous: OpsFunnel;
  series: (OpsFunnel & { bucket: string })[];
  cancelReasons: { by: string; reason: string; count: number }[];
  heatmap: { dow: number; hour: number; requests: number; unserved: number }[];
  topPickups: { name: string; requests: number; completed: number; unserved: number }[];
  byRideType: (OpsFunnel & { name: string })[];
}

export interface DriverScore {
  id: string;
  name?: string;
  phone?: string;
  plate: string | null;
  status?: string;
  lifetimeRating: number | null;
  debt: number;
  offers: number;
  accepted: number;
  rejected: number;
  acceptanceRate: number | null;
  timedOut: number;
  assigned: number;
  completed: number;
  driverCancelled: number;
  cancelRate: number | null;
  earnings: number;
  platformEarning: number;
  periodRating: number | null;
  ratings: number;
  avgMinutesToArrive: number | null;
  onlineHours: number | null;
  utilisation: number | null;
}

export interface DriverAnalytics {
  window: { from: string; to: string };
  summary: {
    approvedDrivers: number;
    onlineNow: number;
    activeDrivers: number;
    tripsPerActiveDriver: number | null;
    earningsPerActiveDriver: number | null;
    acceptanceRate: number | null;
    avgUtilisation: number | null;
  };
  drivers: DriverScore[];
  total: number;
}

export interface RiderAnalytics {
  window: { from: string; to: string; groupBy: GroupBy };
  summary: {
    totalRiders: number;
    ridersWithTrips: number;
    activeRiders: number;
    repeatRiders: number;
    repeatRate: number | null;
    tripsPerRider: number | null;
    spendPerRider: number | null;
  };
  series: { bucket: string; signups: number; newRiders: number }[];
  cohorts: {
    cohort: string;
    size: number;
    retention: { offset: number; riders: number; rate: number }[];
  }[];
}

export type DriverSort = "earnings" | "completed" | "acceptance" | "cancellations" | "rating";

async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await api.get<ApiEnvelope<T>>(url, {
    params: params ? cleanParams(params) : undefined,
  });
  return unwrap(res);
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const res = await api.post<ApiEnvelope<T>>(url, body);
  return unwrap(res);
}

export const consoleApi = {
  live: () => get<LiveBoard>("/admin/ops/live"),
  today: () => get<TodayKpis>("/admin/ops/today"),
  demand: (minutes: number, rideTypeId?: string) =>
    get<{ since: string; minutes: number; requests: DemandPoint[] }>("/admin/ops/demand", {
      minutes,
      rideTypeId,
    }),
  inbox: () => get<Inbox>("/admin/ops/inbox"),
  search: (q: string) => get<SearchResults>("/admin/ops/search", { q }),

  cancelTrip: (id: string, body: { reason: string; fault: "rider" | "driver" | "platform" }) =>
    post<{ trip: unknown }>(`/admin/trips/${id}/cancel`, body),
  reassignTrip: (id: string, body: { reason: string; driverId?: string }) =>
    post<{ trip: unknown; mode: "assigned" | "searching" }>(`/admin/trips/${id}/reassign`, body),
  forceCompleteTrip: (id: string, body: { reason: string }) =>
    post<{ trip: unknown }>(`/admin/trips/${id}/force-complete`, body),
  closeUnpaidTrip: (id: string, body: { reason: string; keepWallet: boolean }) =>
    post<{ trip: unknown; refund: { amount: number } | null }>(`/admin/trips/${id}/close-unpaid`, body),
  refundTripWallet: (id: string, body: { reason: string }) =>
    post<{ trip: unknown; refund: { amount: number } }>(`/admin/trips/${id}/refund-wallet`, body),

  finance: (w: Window) => get<FinanceOverview>("/admin/finance/overview", { ...w }),
  cashReconciliation: (w: Window) =>
    get<CashReconciliation>("/admin/finance/cash-reconciliation", { ...w }),
  operations: (w: Window) => get<OpsAnalytics>("/admin/analytics/operations", { ...w }),
  drivers: (w: Window & { sort?: DriverSort; limit?: number; driverId?: string }) =>
    get<DriverAnalytics>("/admin/analytics/drivers", { ...w }),
  riders: (w: Window) => get<RiderAnalytics>("/admin/analytics/riders", { ...w }),
};
